import { z } from 'zod';

/**
 * Shared enumerations.
 *
 * These are the single definition used by the Zod schemas, the Drizzle columns,
 * and the client. Rubric E2: one schema, both sides, never a duplicate that
 * drifts.
 */

/**
 * Roles, per BUILD_PLAN section 3.
 *
 * `club_admin` is RESERVED and not implemented in v1. The value exists from the
 * first migration so a club rollout does not require a permission system
 * migration later (D5). Any code path encountering it throws with error code
 * ROLE_NOT_IMPLEMENTED, which renders as themed copy, never as an exception.
 */
export const RoleSchema = z.enum([
  'owner',
  'head_coach',
  'assistant_coach',
  'team_manager',
  'scorekeeper',
  'guardian',
  'club_admin',
]);
export type Role = z.infer<typeof RoleSchema>;

/** Roles a v1 code path may actually act on. */
export const IMPLEMENTED_ROLES = [
  'owner',
  'head_coach',
  'assistant_coach',
  'team_manager',
  'scorekeeper',
  'guardian',
] as const satisfies readonly Role[];

/**
 * Who may see what, per BUILD_PLAN section 3.
 *
 * Structured data rather than prose so the check is one lookup instead of a
 * conditional a reviewer has to re-derive. Rubric B2 is an S0, and an S0 should
 * not depend on someone remembering that a team manager sees allergies but not
 * medical notes.
 */
export const MEDICAL_ACCESS: Readonly<
  Record<Role, { readonly allergies: boolean; readonly medicalNotes: boolean }>
> = {
  owner: { allergies: true, medicalNotes: true },
  head_coach: { allergies: true, medicalNotes: true },
  // ADR-0108, amending section 3: the assistant coach holds the head coach's
  // permission set, medical notes included (owner decisions, 22 and 24 October
  // 2026). Still audited on every read, through the same route.
  assistant_coach: { allergies: true, medicalNotes: true },
  team_manager: { allergies: true, medicalNotes: false },
  scorekeeper: { allergies: false, medicalNotes: false },
  // A guardian sees their OWN player in full. This table governs access to
  // ANOTHER family's data, which for a guardian is never.
  guardian: { allergies: false, medicalNotes: false },
  club_admin: { allergies: false, medicalNotes: false },
};

/**
 * What each role may read of a roster, per BUILD_PLAN section 3.
 *
 * Three answers, not two, because a Scorekeeper is a real case rather than an
 * edge one: the row reads "Sees jersey numbers and names. No contact info, no
 * medical data", and that role is handed to a stranger through a texted link
 * (BUILD_PLAN 4.1, rubric B5). Giving them the same payload as a coach because
 * both are "allowed" would be the whole point of the role missed.
 *
 * A Guardian gets `none`, which is surprising enough to state plainly: section
 * 3's Guardian row enumerates own player, own RSVPs, team schedule, team
 * messages, own meal assignment and published stats, and the team roster is not
 * among them. That is the plan followed rather than a judgement made here. If
 * it is wrong it is wrong in the plan, and this table is where to change it.
 *
 * Structured for the same reason MEDICAL_ACCESS is: a lookup a reviewer can
 * check against the plan in one glance, rather than a conditional they have to
 * re-derive.
 */
export const ROSTER_ACCESS: Readonly<Record<Role, RosterAccess>> = {
  owner: 'full',
  head_coach: 'full',
  assistant_coach: 'full',
  team_manager: 'full',
  // Names and numbers. Not position, and above all not `status`, which carries
  // `injured`: a fact about a named child that has no business reaching
  // somebody holding a one-game link.
  scorekeeper: 'names_and_numbers',
  // Owner decision, ADR-0020, amending BUILD_PLAN section 3.
  //
  // Section 3 does not list the roster among a Guardian's rows, and the first
  // implementation followed that literally and gave them nothing. The owner
  // overruled it: parents already know these children by sight and by name from
  // the sideline, so withholding a list of first names and numbers protects
  // nothing and reads as the app being evasive.
  //
  // Still withheld, and this is the part that matters: `status`, which carries
  // `injured`, and `position`. Same projection as a scorekeeper.
  guardian: 'names_and_numbers',
  // Never consulted. A club_admin membership throws before any read is
  // attempted, and this entry exists so the table stays total.
  club_admin: 'none',
};

export type RosterAccess = 'full' | 'names_and_numbers' | 'none';

/**
 * Whether a role may CHANGE the roster.
 *
 * A different question from `ROSTER_ACCESS`, and kept in a different table
 * because conflating the two is the obvious mistake and an expensive one.
 * `ROSTER_ACCESS` answers "which fields does this person see". This answers
 * "may this person add, edit or remove a player".
 *
 * Team Manager has `full` READ access and no write. BUILD_PLAN section 3 gives
 * it "roster read" in as many words, and reusing the read map here would have
 * handed a Team Manager the delete. Assistant Coach had the same row until
 * ADR-0108 gave it the head coach's permission set, write included.
 *
 * Guardian is false, and that is not the same as saying a guardian may change
 * nothing. Section 3 gives them "own player's profile", which is a scoped write
 * to one row with its own check against `guardianships`. It is a different
 * capability and it must not arrive by way of this table.
 *
 * `scorekeeper` and `club_admin` are false for the reasons they are elsewhere:
 * a scorekeeper holds a link to one game, and a reserved role throws before any
 * of this is consulted.
 */
export const ROSTER_WRITE: Readonly<Record<Role, boolean>> = {
  owner: true,
  head_coach: true,
  assistant_coach: true,
  team_manager: false,
  scorekeeper: false,
  guardian: false,
  club_admin: false,
};

/**
 * Whether a role may hold a password at all.
 *
 * A third table rather than a reuse of either above it, for the same reason
 * there are already two: these are three different questions and the cost of
 * conflating them is paid in a permission nobody meant to grant.
 *
 * ADR-0029 records the decision this encodes. `BUILD_PLAN.md` 4.1 specifies
 * passwordless authentication and "No passwords at all", and the owner has
 * overridden that for STAFF. The plan's stated reasoning is preserved exactly
 * where it applies: "parents will use this four times a month, they will not
 * remember a password". A guardian therefore keeps the magic link, and this
 * table is what makes that structural rather than a convention a future route
 * can forget.
 *
 * A guardian who is also a coach somewhere holds two memberships and one
 * account, and `users` is deliberately not tenant-scoped, so the question a
 * caller must ask is whether ANY of a person's memberships is a staff one. It
 * is not "what is their role", because a person does not have one.
 *
 * `club_admin` is false like everywhere else: a reserved role throws before any
 * of this is consulted, and a reserved role that could hold a password would be
 * a live credential for a permission set that does not exist yet.
 */
export const PASSWORD_ELIGIBLE: Readonly<Record<Role, boolean>> = {
  owner: true,
  head_coach: true,
  assistant_coach: true,
  team_manager: true,
  scorekeeper: true,
  guardian: false,
  club_admin: false,
};

/**
 * Which membership to act under when the request does not say.
 *
 * Most capable first, and SECOND to season recency. One human with one email is
 * routinely a coach on one team and a guardian on another (the `users` table
 * says so in its own header), and "which team am I looking at" has to have a
 * deterministic answer.
 *
 * It did not. The default took whatever the season sort returned first, and
 * that sort tied completely whenever `starts_on` was unset, which is always. A
 * head coach who is also a guardian elsewhere could land on the guardian
 * membership, be refused the roster, and read a screen telling him the roster
 * is with his coaches. There is no switcher in the client yet, so that was
 * terminal.
 *
 * This docstring then said "most capable rather than most recent", and
 * `resolveMembership` implemented exactly that, which turned out to be a
 * permission leak: a role from a CLOSED season outranked the current one, so a
 * parent who coached last year read this year's roster as a coach. Season
 * recency wins now, and this array breaks the tie among memberships that are
 * equally recent. Within one season the original reasoning stands: a guardian
 * opening the app wants their own child, a coach has a roster to manage, and
 * landing them on the smaller job is the more expensive mistake.
 *
 * `club_admin` is last and is filtered before this is consulted. It is present
 * so the record stays total and the array stays a permutation of `Role`.
 */
export const ROLE_PRECEDENCE = [
  'owner',
  'head_coach',
  'assistant_coach',
  'team_manager',
  'scorekeeper',
  'guardian',
  'club_admin',
] as const satisfies readonly Role[];

/** Lower sorts first. Total over `Role`, so no membership is unorderable. */
export const ROLE_RANK: Readonly<Record<Role, number>> = Object.fromEntries(
  ROLE_PRECEDENCE.map((role, index) => [role, index]),
) as Record<Role, number>;

/** Allergen list, per BUILD_PLAN section 4.7. Structured, never freeform. */
export const AllergenSchema = z.enum([
  'peanut',
  'tree_nut',
  'dairy',
  'egg',
  'soy',
  'wheat_gluten',
  'fish',
  'shellfish',
  'sesame',
  'other',
]);
export type Allergen = z.infer<typeof AllergenSchema>;

/**
 * Severity, ordered least to most serious. The order is load-bearing: the
 * allergy card escalates severe cases visually, and comparing by index is how.
 */
export const AllergySeveritySchema = z.enum([
  'avoid_preference',
  'intolerance',
  'allergy',
  'severe_anaphylaxis',
]);
export type AllergySeverity = z.infer<typeof AllergySeveritySchema>;

export const ALLERGY_SEVERITY_ORDER = [
  'avoid_preference',
  'intolerance',
  'allergy',
  'severe_anaphylaxis',
] as const satisfies readonly AllergySeverity[];

/**
 * Dietary preference. NOT medical, and displayed differently (section 4.7).
 * Kept in its own enum and its own table so it is structurally hard to leak one
 * while protecting the other.
 */
export const DietaryPreferenceSchema = z.enum(['vegetarian', 'vegan', 'halal', 'kosher', 'other']);
export type DietaryPreference = z.infer<typeof DietaryPreferenceSchema>;

export const EventTypeSchema = z.enum([
  'game',
  'practice',
  'tournament',
  'scrimmage',
  'team_event',
  'other',
]);
export type EventType = z.infer<typeof EventTypeSchema>;

/**
 * The fixture types a scorebook may be started on: scored contests (ADR-0043).
 *
 * A scrimmage's book never counts toward season stats, and a tournament's
 * inclusion is a per-game option; both are stats-engine decisions recorded in
 * the ADR, not enforced here. A practice is planned to get its own surface (a
 * running clock, intervals, a horn), which is not this one.
 */
export const SCOREBOOK_EVENT_TYPES: ReadonlySet<EventType> = new Set<EventType>([
  'game',
  'scrimmage',
  'tournament',
]);

export const RsvpStatusSchema = z.enum(['going', 'not_going', 'maybe']);
export type RsvpStatus = z.infer<typeof RsvpStatusSchema>;

export const UniformSchema = z.enum(['home', 'away', 'specified']);
export type Uniform = z.infer<typeof UniformSchema>;

/** Per D9. Defaults to curated. See BUILD_PLAN 4.13 for why. */
export const PlayerStatsVisibilitySchema = z.enum(['off', 'curated', 'full']);
export type PlayerStatsVisibility = z.infer<typeof PlayerStatsVisibilitySchema>;

/**
 * Notification presets, per D18.
 *
 * The per-category, per-channel matrix exists underneath because the model
 * needs it, but no parent will ever configure it. These three are what the
 * interface leads with.
 */
export const NotificationPresetSchema = z.enum([
  'everything',
  'important_only',
  'game_reminders_only',
  'custom',
]);
export type NotificationPreset = z.infer<typeof NotificationPresetSchema>;

export const AuthTokenTypeSchema = z.enum([
  'magic_link',
  'invite',
  'scorekeeper',
  'password_setup',
  /**
   * An Owner's invitation for a named person to create and lead a new team
   * (ADR-0073). Single use, like `invite`, and distinct from it: an `invite`
   * joins an EXISTING team's roster through a guardianship; this one creates
   * the team itself, and nothing else in the product does that from a token.
   */
  'team_invite',
  /**
   * A family finishing a team-code signup (ADR-0113). Single use, sixty
   * minutes, no user: nothing is a person until a coach approves the request
   * the token lets the family submit. Its scope is written by the server
   * (code, organization, team, season, email, name) and read back by the
   * public peek and submit routes, never from a body.
   */
  'family_signup',
  /** Long-lived, revocable token for one person's subscribed team calendar. */
  'calendar_feed',
]);
export type AuthTokenType = z.infer<typeof AuthTokenTypeSchema>;

/**
 * Which sport a team plays.
 *
 * Basketball first, other sports later (BUILD_PLAN section 1), and "later"
 * arrived: soccer, because two real children start practice on 18 August 2026.
 *
 * This is the column a caller will read to choose between
 * `@good-season/stats-basketball` and a soccer engine FROM DATA rather than
 * from a hardcoded import.
 *
 * WILL, not does. `packages/stats-soccer` is on a separate unmerged branch and
 * does not exist here, and nothing in this repository reads `teams.sport` yet:
 * the only writes are in `team:create` and `/api/team` does not project it. A
 * fresh-context pass caught the previous version of this paragraph claiming
 * otherwise, which is the shape of comment this project keeps paying for.
 *
 * A team, not an organization. A club can run both, and this repository's own
 * first club is a basketball association whose families also play soccer.
 * Putting it on the organization would force a second club for the same people
 * and split their roster in half.
 */
export const SportSchema = z.enum(['basketball', 'soccer']);
export type Sport = z.infer<typeof SportSchema>;

/**
 * The default period structure per sport, as a starting point and nothing more.
 *
 * BUILD_PLAN 5.2 confirms basketball's: two halves. Soccer's is deliberately
 * NOT authoritative, because youth soccer genuinely varies and the owner's own
 * answer was "four 15-minute quarters or maybe they were 12-minute. I don't
 * know. It needs to be customizable." So this is what a new team starts with
 * and every value is editable per game, including at the field.
 *
 * `onCourtCount` is 7 for soccer because youth 7v7 is the format in front of
 * us. 9v9 and 11v11 work today: nothing anywhere validates the number.
 */
export const SPORT_DEFAULTS: Readonly<
  Record<
    Sport,
    {
      readonly periods: number;
      readonly periodMinutes: number;
      readonly onCourtCount: number;
      /** Fouls charged to a player before they must leave. 0: the league does not foul anybody out. */
      readonly disqualifyingFouls: number;
      /** Team fouls in one period that put the OTHER side in the bonus. 0: no bonus. */
      readonly teamFoulsForBonus: number;
      /** Team fouls in one period for the double bonus (two shots). 0: none. */
      readonly teamFoulsForDoubleBonus: number;
    }
  >
> = {
  /*
   * BUILD_PLAN 5.2's confirmed defaults: five fouls to disqualify. The bonus
   * pair is the two-halves convention (one-and-one at seven, two shots at
   * ten), editable per game like everything else here.
   */
  basketball: {
    periods: 2,
    periodMinutes: 16,
    onCourtCount: 5,
    disqualifyingFouls: 5,
    teamFoulsForBonus: 7,
    teamFoulsForDoubleBonus: 10,
  },
  soccer: {
    periods: 4,
    periodMinutes: 12,
    onCourtCount: 7,
    disqualifyingFouls: 0,
    teamFoulsForBonus: 0,
    teamFoulsForDoubleBonus: 0,
  },
};

export const PlayerStatusSchema = z.enum(['active', 'inactive', 'injured']);
export type PlayerStatus = z.infer<typeof PlayerStatusSchema>;

export const MediaKindSchema = z.enum(['player_photo', 'team_logo', 'other']);
export type MediaKind = z.infer<typeof MediaKindSchema>;

/**
 * Where a game is in its life.
 *
 * `scheduled` is a fixture with a config and no events yet. `live` is a game
 * being scored right now, which is what a second device opening the same game
 * needs to know before it offers to take over (BUILD_PLAN 5.8). `final` is
 * finalised by a named person at a named time, which is the pair the migration
 * enforces together.
 *
 * There is no `cancelled`. Cancellation lives on the schedule event, because a
 * game that never happened is a schedule fact and not a scorebook one.
 */
export const GameStatusSchema = z.enum(['scheduled', 'live', 'final']);
export type GameStatus = z.infer<typeof GameStatusSchema>;

/**
 * Which side an event belongs to.
 *
 * `them` events carry an opponent number rather than a player id, because the
 * other team's roster is not ours to hold (BUILD_PLAN 5.3).
 */
export const EventSideSchema = z.enum(['us', 'them']);
export type EventSide = z.infer<typeof EventSideSchema>;
